/**
 * Given an organization's location fields and the full list of locations,
 * returns the most specific matching Location — city first, then province, then country.
 */

// Maps Canadian province codes (e.g. "ON") to full names (e.g. "Ontario")
const PROVINCE_CODE_TO_NAME: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NT: 'Northwest Territories',
  NS: 'Nova Scotia',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

interface OrgLocationFields {
  cityName?: string | null;
  provinceCode?: string | null;
  province?: { name?: string | null; code?: string | null } | null;
  city?: { name?: string | null } | null;
}

interface LocationRecord {
  id: string;
  name: string;
  locationCode?: string | null;
  hierarchyLevel?: number;
  parentLocationId?: string | null;
}

export function resolveOrgLocation(
  org: OrgLocationFields,
  locations: LocationRecord[]
): LocationRecord | null {
  const cityName =
    org.cityName?.trim() || org.city?.name?.trim() || null;

  const provinceDisplayName =
    org.province?.name?.trim() ||
    (org.provinceCode ? PROVINCE_CODE_TO_NAME[org.provinceCode.toUpperCase()] : null) ||
    null;

  // 1. Try to match by city name (hierarchyLevel 2 preferred)
  if (cityName) {
    const cityLower = cityName.toLowerCase();
    const cityMatch = locations.find(
      (l) =>
        (l.hierarchyLevel === 2 || (l.hierarchyLevel !== 0 && l.hierarchyLevel !== 1)) &&
        l.name.toLowerCase() === cityLower
    );
    if (cityMatch) return cityMatch;
  }

  // 2. Try to match by province name (hierarchyLevel 1)
  if (provinceDisplayName) {
    const provLower = provinceDisplayName.toLowerCase();
    const provMatch = locations.find(
      (l) => l.hierarchyLevel === 1 && l.name.toLowerCase() === provLower
    );
    if (provMatch) return provMatch;
  }

  // 3. Fall back to Canada
  const canada = locations.find((l) => l.locationCode === 'CA');
  return canada ?? null;
}



